import React, { useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { useEditor, useValue } from "../../editor-context"
import useEventListener from "../../hooks/use-event-listener"
import useTransitionElement from "../../hooks/use-transition-element"
import { findDOMNode } from "@convertkit/slate-react"

const unknown = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  height: 0,
  width: 0
}

import Toolbar from "./toolbar-content"

import "./toolbar.css"
import isHotkey from "is-hotkey"
import { getRootBlock } from "../../utils/helpers"

function getPosition(target, reference) {
  const offsetParent = getOffsetParent(target)
  const offsetRect = getBoundingClientRect(offsetParent)
  const referenceRect = getBoundingClientRect(reference)
  const editor = reference.closest("[data-slate-editor]")
  const editorRect = getBoundingClientRect(editor)

  let left = editorRect.left - offsetRect.left
  let top = referenceRect.top - offsetRect.top + offsetParent.scrollTop - 36

  return { top, left }
}

function getBoundingClientRect(element) {
  if (!element) return unknown
  return element.getBoundingClientRect()
}

function getOffsetParent(element) {
  return (element && element.offsetParent) || document.documentElement
}

const TEMPORARY_SUPPORTED_BLOCKS = /para|list|heading/

function SelectionToolbar({ buttons, exclude = [] }) {
  const editor = useEditor()
  const value = useValue()
  const toolbar = useRef()
  const rootBlock = getRootBlock(value, value.startBlock)

  const [position, setPosition] = useState(unknown)

  const [transitioning, startTransition] = useTransitionElement(toolbar.current)

  const [visible, setVisible] = useState(false)
  const [menu, setMenu] = useState(null)

  const openMenu = menu => {
    setMenu(menu)
  }

  const closeMenu = () => {
    setMenu(null)
  }

  const recalculate = () => {
    const rootNode = findDOMNode(rootBlock)

    setPosition(getPosition(toolbar.current, rootNode))
  }

  useEffect(() => {
    if (!rootBlock) return
    const type = rootBlock.type || ""
    if (type.match(TEMPORARY_SUPPORTED_BLOCKS)) {
      recalculate()
      if (!visible) show()
    } else {
      if (visible) hide()
    }
  }, [rootBlock])

  const show = () => {
    startTransition()
    setVisible(true)
  }

  const hide = () => {
    startTransition()
    setVisible(false)
    // Wait until the toolbar is hidden before closing the menu.
    setTimeout(() => {
      closeMenu()
    }, 300)
  }

  const handleKeyDown = event => {
    event.stopPropagation()

    if (!visible) return

    buttons.find((button, index) => {
      if (!button.shortcut) return false

      if (isHotkey(button.shortcut, event)) {
        openMenu(index)
        return true
      }
      return false
    })
  }

  useEventListener(document, "keydown", handleKeyDown)

  const type = value.focusBlock && value.focusBlock.type

  const className = classNames(
    "convertkit-editor-selection-toolbar experimental",
    {
      visible: visible && !exclude.includes(type),
      transitioning
    }
  )

  return (
    <Toolbar
      ref={toolbar}
      className={className}
      editor={editor}
      buttons={buttons}
      menu={{
        active: menu != null,
        index: menu,
        close: closeMenu,
        open: openMenu
      }}
      style={position}
    />
  )
}

export default SelectionToolbar
